import { getReportFooterText } from '../reportFooter';
export interface OutstandingPoItem {
  logFormNumber: string;
  description?: string | null;
  prDate: string;
  daysOnOrder?: number | null;
  lin?: string | null;
  nsn?: string | null;
  nomenclature: string;
  qtyReceived?: number | null;
  qtyOrdered?: number | null;
  price?: number | null;
}

export type OutstandingPoPriceDisplayMode = 'unit' | 'total-line';

export interface OutstandingPosTemplateOptions {
  generatedAt: Date;
  items: OutstandingPoItem[];
  priceDisplayMode?: OutstandingPoPriceDisplayMode;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return sanitizeText(value);
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const buildSections = (
  items: OutstandingPoItem[],
  priceDisplayMode: OutstandingPoPriceDisplayMode = 'unit',
): string => {
  if (items.length === 0) {
    return `
      <section class="po-section">
        <div class="empty-row">
          No outstanding purchase order line items were found.
        </div>
      </section>
    `;
  }

  const byDocument = new Map<string, OutstandingPoItem[]>();
  for (const item of items) {
    const key = item.logFormNumber || 'Unknown';
    const existing = byDocument.get(key);
    if (existing) {
      existing.push(item);
    } else {
      byDocument.set(key, [item]);
    }
  }

  return Array.from(byDocument.entries())
    .map(([logFormNumber, rows]) => {
      const description = rows[0]?.description ?? null;
      const prDate = rows[0]?.prDate ?? '';
      const daysOnOrder = rows[0]?.daysOnOrder ?? null;

      const lineRows =
        rows.length === 0
          ? `
              <tr>
                <td colspan="6" class="empty-row">
                  No outstanding line items for this purchase order.
                </td>
              </tr>
            `
          : rows
              .map(
                (row) => `
              <tr>
                <td class="cell-lin">${sanitizeText(row.lin)}</td>
                <td class="cell-nsn">${sanitizeText(row.nsn)}</td>
                <td class="cell-nomenclature">${sanitizeText(row.nomenclature)}</td>
                <td class="numeric center-align">${sanitizeText(
                  row.qtyReceived !== null && row.qtyReceived !== undefined
                    ? String(row.qtyReceived)
                    : '',
                )}</td>
                <td class="numeric center-align">${sanitizeText(
                  row.qtyOrdered !== null && row.qtyOrdered !== undefined
                    ? String(row.qtyOrdered)
                    : '',
                )}</td>
                <td class="numeric center-align">${formatCurrency(row.price ?? null)}</td>
              </tr>
            `,
              )
              .join('');

      return `
        <section class="po-section">
          <table class="po-header-table" data-sheet-name="${sanitizeText(logFormNumber)}">
            <colgroup>
              <col style="width:20%">
              <col style="width:14%">
              <col style="width:13%">
              <col style="width:16%">
              <col style="width:18%">
              <col style="width:19%">
            </colgroup>
            <tbody>
              <tr>
                <td class="label-cell">Log Form Number</td>
                <td class="value-cell">${sanitizeText(logFormNumber)}</td>
                <td class="label-cell">Order Date</td>
                <td class="value-cell date-value-cell">${formatDate(prDate)}</td>
                <td class="label-cell">Days on Order</td>
                <td class="value-cell days-value-cell">${
                  daysOnOrder != null ? sanitizeText(String(daysOnOrder)) : '—'
                }</td>
              </tr>
              <tr>
                <td class="label-cell">Description</td>
                <td class="value-cell" colspan="5">${sanitizeText(description)}</td>
              </tr>
            </tbody>
          </table>

          <table class="po-lines-table" data-sheet-name="${sanitizeText(logFormNumber)} Lines">
            <thead>
              <tr>
                <th>LIN</th>
                <th>NSN</th>
                <th>Nomenclature</th>
                <th class="center-align">QTY Received</th>
                <th class="center-align">QTY On Order</th>
                <th class="center-align">${priceDisplayMode === 'total-line' ? 'Total Cost' : 'Price'}</th>
              </tr>
            </thead>
            <tbody>
              ${lineRows}
            </tbody>
          </table>
        </section>
      `;
    })
    .join('');
};

export const buildOutstandingPosReportHtml = ({
  generatedAt,
  items,
  priceDisplayMode = 'unit',
}: OutstandingPosTemplateOptions): string => {
  const generatedLabel = formatDateTime(generatedAt);
  const priceModeLabel = priceDisplayMode === 'total-line' ? 'Total Line Value' : 'Unit Price';
  const sections = buildSections(items, priceDisplayMode);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Outstanding POs</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 16px;
      }

      .title {
        font-size: 24px;
        font-weight: 700;
        margin: 0;
        text-align: center;
      }

      .generated {
        margin-top: 4px;
        font-size: 14px;
        color: #4a5c72;
        text-align: right;
      }

      .price-mode {
        margin-top: 2px;
        font-size: 12px;
        color: #5c6f85;
        text-align: right;
      }

      .po-section {
        margin-bottom: 24px;
        page-break-inside: avoid;
      }

      .po-header-table {
        width: 100%;
        table-layout: fixed;
        border-collapse: collapse;
        border: 1px solid #d7e3f4;
        margin-bottom: 4px;
      }

      .po-header-table td {
        padding: 6px 8px;
        font-size: 13px;
        border-right: 1px solid #d7e3f4;
        vertical-align: middle;
        overflow-wrap: break-word;
        word-break: break-word;
      }

      .po-header-table td:last-child {
        border-right: none;
      }

      .po-header-table tbody tr:not(:last-child) td {
        border-bottom: 1px solid #d7e3f4;
      }

      .label-cell {
        font-weight: 600;
        background: #f1f5ff;
        white-space: nowrap;
      }

      .value-cell {
        font-weight: 400;
      }

      .date-value-cell,
      .days-value-cell {
        text-align: center;
        white-space: nowrap;
      }

      .po-lines-table {
        width: 100%;
        table-layout: fixed;
        border-collapse: collapse;
        border: 1px solid #d7e3f4;
      }

      .po-lines-table thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 8px 10px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
        vertical-align: middle;
      }

      .po-lines-table tbody td {
        padding: 8px 10px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
        vertical-align: middle;
      }

      .po-lines-table tbody tr:last-child td {
        border-bottom: none;
      }

      .po-lines-table tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: center;
        font-variant-numeric: tabular-nums;
      }

      .center-align {
        text-align: center !important;
      }

      .cell-lin {
        text-transform: uppercase;
        text-align: left;
      }

      .cell-nsn {
        text-transform: uppercase;
        text-align: left;
        white-space: nowrap;
      }

      .po-lines-table th:nth-child(1),
      .po-lines-table td:nth-child(1) {
        width: 10%;
      }

      .po-lines-table th:nth-child(2),
      .po-lines-table td:nth-child(2) {
        width: 18%;
      }

      .po-lines-table th:nth-child(3),
      .po-lines-table td:nth-child(3) {
        width: 36%;
      }

      .po-lines-table th:nth-child(4),
      .po-lines-table td:nth-child(4),
      .po-lines-table th:nth-child(5),
      .po-lines-table td:nth-child(5),
      .po-lines-table th:nth-child(6),
      .po-lines-table td:nth-child(6) {
        width: 12%;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 24px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <h1 class="title">Outstanding POs</h1>
      <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>
      <div class="price-mode">Price Basis: ${escapeHtml(priceModeLabel)}</div>
    </header>

    ${sections}

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
